<script lang="ts" setup>
import { ref, onMounted } from 'vue';
import { formatDateTime } from '@services/utils/Formatters';
import OperationsRepository, {
  OperationSearchData,
} from '@repositories/operations.repository';
import CompaniesRepository from "@repositories/companies.repository";
import AgentsDataProviderRepository from "@repositories/agents.repository";
import { BModal } from 'bootstrap-vue-next';
import ModalContentClient from './ModalContentClient.vue';
import ModalContentPolicyPackage from './ModalContentPolicyPackage.vue';
import OperationsFilters from '../../components/Operaciones/OperationsFilters.vue';
import Store from '@store/dataProvider';
import { storeToRefs } from 'pinia';
import { IPolicyPackage } from '../../types/policy.type';
import { IClient } from '../../types/client.type';
import { ICompany } from "../../types/company.type";
import { IAgents } from "../../types/agents.type";
import { useI18n } from 'vue-i18n'
import DataTable from '@components/DataTable.vue';

const isLoadingOperations = ref<boolean>(false);
const isDownloadingExcel = ref<boolean>(false);

const { t } = useI18n();
const store = Store();
const { operations }: any = storeToRefs(store);

const operationsRepository = new OperationsRepository();
const agentsProviderController = new AgentsDataProviderRepository();
const operationsData = ref([]);
const paginationData = ref();
const currentPage = ref<number>(1);
const isModalOpen = ref<boolean>(false);
const selectedClientInfo = ref<IClient | null>(null);
const selectedPolicyInfo = ref<IPolicyPackage | null>(null);
const modalType = ref<string>('');
const companiesData = ref<Array<ICompany>>([]);
const agentsData = ref<Array<IAgents>>([]);
const filterData = ref<OperationSearchData | null>(null);

const getOperationsPackages = async (page: number) => {
  isLoadingOperations.value = true;
  try {
    const { data } = await operationsRepository.getOperationsPackages({
      page,
      searchData: filterData.value,
    });
    operationsData.value = data.data ?? [];
    paginationData.value = data.pagination;
    store.setProp({ key: 'operations', value: operationsData.value });
  } finally {
    isLoadingOperations.value = false;
  }
};

const getAgentsAPI = async () => {
  const response = await agentsProviderController.indexAgents();
  if (!response) return;
  if (Array.isArray(response?.data)) {
    agentsData.value = response.data;
  } else {
    agentsData.value = [];
  }
};

const getCompaniesAPI = async () => {
  const { companies } = await CompaniesRepository.getCompanies(true);
  if (Array.isArray(companies)) {
    companiesData.value = companies.map((e: ICompany) => ({
      ...e,
      nombre: e.name,
    }));
  }
};

const handleSearch = async (filters: OperationSearchData) => {
  filterData.value = filters;
  getOperationsPackages(1);
};

const resetSearch = async () => {
  filterData.value = null;
  getOperationsPackages(1);
};

onMounted(async () => {
  getOperationsPackages(1);
  getCompaniesAPI();
  getAgentsAPI();
});

const fields: any = ref([
  {
    key: 'id',
    label: t('operaciones.operaciones.id'),
    sortable: true,
  },
  {
    key: 'createdAt',
    label: t('operaciones.operaciones.fecha_hora_de_cracion'),
    formatter: (item: string) => formatDateTime(item),
  },
  {
    key: 'client',
    label: t('global.cliente'),
    formatter: (item: any) => item?.tradeName,
  },
  {
    key: 'companyGPS',
    label: t('operaciones.operaciones.empresa_gps'),
    formatter: (item: any) => item?.description,
  },
  {
    key: 'clientInfo',
    label: t('global.cliente'),
  },
  {
    key: 'policyInfo',
    label: t('global.poliza'),
  },
]);

const onChangePage = async (page: any) => {
  currentPage.value = page;
  getOperationsPackages(page);
};
const handleSelectClient = (clientInfo: any) => {
  selectedClientInfo.value = clientInfo.client;
  modalType.value = 'modalClient';
  isModalOpen.value = true;
};
const handleSelectPolicy = async (policyInfo: any) => {
  selectedPolicyInfo.value = operations.value.find(
    (e: any) => policyInfo.id === e.id,
  );
  const { data } = await operationsRepository.getOperationPackageNumbersGuides(
    policyInfo.id,
  );

  if (selectedPolicyInfo.value) {
    selectedPolicyInfo.value = {
      ...selectedPolicyInfo.value,
      numbersGuides: data.numbersGuides as string,
    };
  }
  modalType.value = 'modalPolicy';
  isModalOpen.value = true;
};

const handleClose = () => {
  isModalOpen.value = false;
  selectedClientInfo.value = null;
  selectedPolicyInfo.value = null;
  modalType.value = '';
};
const updateCurrentPolicyClient = (data: any) => {
  if (!selectedPolicyInfo.value) return;
  selectedPolicyInfo.value.client = data;
};
const updateCurrentPolicy = (data: any) => {
  selectedPolicyInfo.value = data;
};

const downloadExcel = async () => {
  isDownloadingExcel.value = true;
  try {
    await operationsRepository.getExcelPolicies({
      typePolicy: "paqueteria",
      toExcel: true,
      searchData: filterData.value,
    });
  } finally {
    isDownloadingExcel.value = false;
  }
};
</script>

<template>
  <Layout :title="t('operaciones.operaciones.operaciones_de_paqueteria')" :textColor="'color-secondary'">
    <template #layout>
      <OperationsFilters
        type-policy-transport="Paqueteria"
        :companies="companiesData"
        :agents="agentsData"
        :start-date-label="t('polizas.polizas.fecha_de_inicio')"
        :end-date-label="t('polizas.polizas.fecha_de_fin')"
        :is-downloading-excel="isDownloadingExcel"
        @search="handleSearch"
        @reset="resetSearch"
        @download="downloadExcel"
      />
      <DataTable
        :loading="isLoadingOperations"
        :fields="fields"
        :items="store.operations"
        @update:model-value-page="onChangePage"
        :totalRows="paginationData?.count"
        :perPage="20"
        :currentPage="paginationData?.current_page"
      >
        <template v-if="true" v-slot:cell(clientInfo)="operationsData">
          <div @click="handleSelectClient(operationsData.data.item)">
            <img
              src="/icons/user_panel.svg"
              style="width: 40px; cursor: pointer"
            />
          </div>
        </template>
        <template v-if="true" v-slot:cell(policyInfo)="operationsData">
          <div @click="handleSelectPolicy(operationsData.data.item)">
            <img
              src="/icons/policy_panel.svg"
              style="width: 40px; cursor: pointer"
            />
          </div>
        </template>
      </DataTable>
      <BModal
        :modelValue="isModalOpen"
        hide-footer
        :centered="true"
        @hide="handleClose"
        static
        :title="
          modalType === 'modalClient'
            ? t('operaciones.operaciones.informacion_del_cliente')
            : t('operaciones.operaciones.informacion_de_la_poliza')
        "
      >
        <template v-if="modalType === 'modalClient'">
          <ModalContentClient
            :clientData="selectedClientInfo"
            v-if="selectedClientInfo"
          />
        </template>
        <template v-else>
          <ModalContentPolicyPackage
            :policyData="selectedPolicyInfo"
            v-if="selectedPolicyInfo"
          />
          <!-- <ModalContentPolicyMX
            v-if="selectedPolicyInfo && selectedPolicyInfo.country.description==='MEXICO'"
            :policyData="selectedPolicyInfo"
            @updateCurrentPolicyClient="updateCurrentPolicyClient($event)"
            @updateCurrentPolicy="updateCurrentPolicy($event)"
          />
		  <ModalContentPolicyAR
            v-else-if="selectedPolicyInfo"
            :policyData="selectedPolicyInfo"
          /> -->
        </template>
      </BModal>
    </template>
  </Layout>
</template>
